/**
 * guidanceOverlay.ts
 *
 * Drawing functions for the ghost guidance outline in the ScanV2 (interactive)
 * capture flow. Drawn by MediaPipe.tsx over the live camera frame; the figure
 * turns green once the pose passes.
 *
 * Three views: front, right, and flex. Geometry and provenance for the flex
 * figure are in sizewall-docs/reference/pose-registry.md.
 *
 * Note: PoseDebugPage carries its own private copies of the front and side
 * drawing functions rather than importing these — the two have drifted, and
 * PoseDebugPage has no flex figure.
 */

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function roundedRectPath(
  c: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  c.moveTo(x + rr, y);
  c.lineTo(x + w - rr, y);
  c.quadraticCurveTo(x + w, y, x + w, y + rr);
  c.lineTo(x + w, y + h - rr);
  c.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  c.lineTo(x + rr, y + h);
  c.quadraticCurveTo(x, y + h, x, y + h - rr);
  c.lineTo(x, y + rr);
  c.quadraticCurveTo(x, y, x + rr, y);
}

export function getHeadAnchor(kpPx: [number, number][]): [number, number] {
  const headIdx = [0, 1, 2, 3, 4];
  const pts = headIdx
    .map((i) => kpPx[i])
    .filter((p): p is [number, number] => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  if (pts.length === 0) return kpPx[0] ?? [0, 0];
  const sx = pts.reduce((acc, [x]) => acc + x, 0);
  const sy = pts.reduce((acc, [, y]) => acc + y, 0);
  return [sx / pts.length, sy / pts.length];
}

export function getPoseBBox(kpPx: [number, number][]) {
  const idx = [0, 5, 6, 9, 10, 11, 12, 15, 16];
  const pts = idx
    .map((i) => kpPx[i])
    .filter((p): p is [number, number] => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  const xs = pts.map(([x]) => x);
  const ys = pts.map(([, y]) => y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return {
    minX, maxX, minY, maxY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY),
  };
}

// ---------------------------------------------------------------------------
// Silhouette drawing
// ---------------------------------------------------------------------------

// Flex arm segment lengths, in the same nominal units as the other limbs.
// They sum to 290 — the front figure's single arm bar — so the flex arm has the
// same total reach, just folded at the elbow.
const FLEX_UPPER_ARM_LEN = 140;
const FLEX_FOREARM_LEN = 150;

export function drawFrontSilhouette(
  ctx: CanvasRenderingContext2D,
  headX: number,
  headY: number,
  scale: number,
  color: string,
  headRadiusMult = 1,
  torsoWidthMult = 1
) {
  const sx = scale * torsoWidthMult;
  const sy = scale;
  const hr = 46 * sy * headRadiusMult;

  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(3, 5 * scale);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  // Head
  ctx.beginPath();
  ctx.arc(headX, headY, hr, 0, Math.PI * 2);
  ctx.stroke();

  const bodyY = headY + hr + 18 * sy;

  // Torso
  const torsoW = 168 * sx;
  const torsoH = 214 * sy;
  ctx.beginPath();
  roundedRectPath(ctx, headX - torsoW / 2, bodyY, torsoW, torsoH, 22 * sy);
  ctx.stroke();

  // Legs
  const legSpread = 41 * sx;
  const legW = 56 * sx;
  const legH = 321 * sy;
  const legY = bodyY + torsoH + 6 * sy;
  for (const side of [-1, 1]) {
    ctx.beginPath();
    roundedRectPath(ctx, headX + side * legSpread - legW / 2, legY, legW, legH, 18 * sy);
    ctx.stroke();
  }

  // Arms — angled 30° outward
  const armOffX  = 60 * sx;
  const armW     = 62 * sx;
  const armH     = 290 * sy;
  const armAngle = 30 * (Math.PI / 180);
  for (const side of [-1, 1]) {
    ctx.save();
    ctx.translate(headX + side * armOffX, bodyY);
    ctx.rotate(-side * armAngle);
    ctx.beginPath();
    roundedRectPath(ctx, -armW / 2, 0, armW, armH, 18 * sy);
    ctx.stroke();
    ctx.restore();
  }
}

/**
 * Biceps-flex pose: same torso, head and legs as the front figure, but each arm
 * is drawn as two segments — upper arm straight out to the side, forearm
 * straight up from the elbow.
 *
 * That 90/90 shape is the pose registry's nominal flex: upper arm at 90 deg
 * abduction (accept 70-110) and forearm at 0 tilt from world-vertical. Drawing
 * the nominal rather than an edge of the accept band gives the user a target to
 * aim at instead of a threshold to sit on.
 *
 * Raised hands reach above the crown, so the figure is taller than the front one —
 * see FIG_H_FLEX / FIG_HEAD_TOP_FLEX, which keep it vertically centred.
 */
export function drawFlexSilhouette(
  ctx: CanvasRenderingContext2D,
  headX: number,
  headY: number,
  scale: number,
  color: string,
  headRadiusMult = 1,
  torsoWidthMult = 1
) {
  const sx = scale * torsoWidthMult;
  const sy = scale;
  const hr = 46 * sy * headRadiusMult;

  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(3, 5 * scale);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  // Head
  ctx.beginPath();
  ctx.arc(headX, headY, hr, 0, Math.PI * 2);
  ctx.stroke();

  const bodyY = headY + hr + 18 * sy;

  // Torso — identical to the front figure
  const torsoW = 168 * sx;
  const torsoH = 214 * sy;
  ctx.beginPath();
  roundedRectPath(ctx, headX - torsoW / 2, bodyY, torsoW, torsoH, 22 * sy);
  ctx.stroke();

  // Legs — identical to the front figure
  const legSpread = 41 * sx;
  const legW = 56 * sx;
  const legH = 321 * sy;
  const legY = bodyY + torsoH + 6 * sy;
  for (const side of [-1, 1]) {
    ctx.beginPath();
    roundedRectPath(ctx, headX + side * legSpread - legW / 2, legY, legW, legH, 18 * sy);
    ctx.stroke();
  }

  // Arms — upper out to the side, forearm up.
  // A limb drawn at rotation t runs from its origin to
  // (-h*sin t, +h*cos t), so t=0 points down, t=+-90deg sideways, t=180deg up.
  const armOffX = 60 * sx;
  const armW = 62 * sx;
  const upperH = FLEX_UPPER_ARM_LEN * sx;
  const foreH = FLEX_FOREARM_LEN * sy;
  const radius = 18 * sy;

  for (const side of [-1, 1]) {
    const shoulderX = headX + side * armOffX;
    const upperAngle = -side * (Math.PI / 2);

    // Upper arm: horizontal, away from the body
    ctx.save();
    ctx.translate(shoulderX, bodyY);
    ctx.rotate(upperAngle);
    ctx.beginPath();
    roundedRectPath(ctx, -armW / 2, 0, armW, upperH, radius);
    ctx.stroke();
    ctx.restore();

    // Forearm: straight up from the elbow
    const elbowX = shoulderX - upperH * Math.sin(upperAngle);
    const elbowY = bodyY + upperH * Math.cos(upperAngle);
    ctx.save();
    ctx.translate(elbowX, elbowY);
    ctx.rotate(Math.PI);
    ctx.beginPath();
    roundedRectPath(ctx, -armW / 2, 0, armW, foreH, radius);
    ctx.stroke();
    ctx.restore();
  }
}

export function drawSideSilhouette(
  ctx: CanvasRenderingContext2D,
  headX: number,
  headY: number,
  scale: number,
  color: string,
  headRadiusMult = 1,
  sideFatnessMult = 1,
  spineX?: number
) {
  const sx = scale * sideFatnessMult;
  const sy = scale;
  const hr = 46 * sy * headRadiusMult;

  // Body and head share one horizontal anchor so the figure stays rigid.
  //
  // These used to be independent: the head sat at headX - hr*0.38 while the
  // torso sat at spineX (or headX - 40), leaving the head ~22px forward of the
  // arms. Worse, the offset drifted in use — the head anchor is the mean of the
  // face keypoints and spineX is the shoulder midpoint, so any head turn slid
  // the two apart and the figure came unglued.
  const bodyCenterX = spineX ?? headX - 40 * sy;
  const headCX = bodyCenterX;
  const headCY = headY;

  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(3, 5 * scale);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  // Head
  ctx.beginPath();
  ctx.arc(headCX, headCY, hr, 0, Math.PI * 2);
  ctx.stroke();

  // Nose indicator
  const noseTipX  = headCX + hr + 28 * sy;
  const noseTipY  = headCY;
  const noseBaseX = headCX + hr - 6 * sy;
  const noseHalfW = 16 * sy;
  ctx.beginPath();
  ctx.moveTo(noseBaseX, noseTipY - noseHalfW);
  ctx.lineTo(noseTipX, noseTipY);
  ctx.lineTo(noseBaseX, noseTipY + noseHalfW);
  ctx.stroke();

  const bodyW = 145 * sx;
  const bodyX = bodyCenterX - bodyW / 2;
  const bodyY = headCY + hr + 4 * sy;
  const bodyR = 26 * sy;
  const torsoH = 201 * sy;

  ctx.beginPath();
  roundedRectPath(ctx, bodyX, bodyY, bodyW, torsoH, bodyR);
  ctx.stroke();

  const legW = bodyW * 0.78;
  const legH = 291 * sy;
  const legX = bodyCenterX - legW / 2;
  const legY = bodyY + torsoH + 6 * sy;
  ctx.beginPath();
  roundedRectPath(ctx, legX, legY, legW, legH, 20 * sy);
  ctx.stroke();

  const armW = 62 * sx;
  const armH = 261 * sy;
  ctx.beginPath();
  roundedRectPath(ctx, bodyCenterX - armW / 2, bodyY, armW, armH, 16 * sy);
  ctx.stroke();
}

// ---------------------------------------------------------------------------
// Main guidance overlay
// ---------------------------------------------------------------------------

export const FIG_H_FRONT     = 651;
export const FIG_H_SIDE      = 594;
export const FIG_HEAD_TOP    = 46;

// Flex reaches higher than the other poses: with the head centre at 0 the crown
// sits at -46, but the raised hands top out at
//   bodyY(64) - FLEX_FOREARM_LEN(150) = -86.
// The feet are unchanged at 605, so the figure is 691 tall and its head centre
// sits 86 below the top. Feeding these to the same centring maths as the other
// views keeps the whole guide on screen instead of clipping the hands.
const FLEX_HAND_TOP     = 46 + 18 - FLEX_FOREARM_LEN;   // hr + gap - forearm = -86
export const FIG_HEAD_TOP_FLEX = -FLEX_HAND_TOP;               // 86
export const FIG_H_FLEX        = 605 + FIG_HEAD_TOP_FLEX;      // 691
const BETA_TOL_DRAW   = 7.0;
const GAMMA_TOL_DRAW  = 10.0;

function keypointsInsideGuidanceBox(
  kpPx: [number, number][],
  natW: number,
  natH: number,
  view: "front" | "right"
) {
  const marginH = 0.05;
  const marginW = view === "front" ? 0.05 : 0.25;
  const x0 = marginW * natW;
  const x1 = (1 - marginW) * natW;
  const y0 = marginH * natH;
  const y1 = (1 - marginH) * natH;
  const idx = [0, 5, 6, 9, 10, 11, 12, 15, 16];
  return idx.every((i) => {
    const p = kpPx[i];
    if (!p) return false;
    return p[0] >= x0 && p[0] <= x1 && p[1] >= y0 && p[1] <= y1;
  });
}

export function drawGuidanceOverlay(
  ctx: CanvasRenderingContext2D,
  kpPx: [number, number][],
  view: "front" | "right" | "flex",
  natW: number,
  natH: number,
  poseOk: boolean,
  headRadiusMult = 1,
  torsoWidthMult = 1,
  sideFatnessMult = 1,
  flipped = false
) {
  const FIG_H =
    view === "right" ? FIG_H_SIDE : view === "flex" ? FIG_H_FLEX : FIG_H_FRONT;
  const headTop = view === "flex" ? FIG_HEAD_TOP_FLEX : FIG_HEAD_TOP;

  const scaleMax = 0.90 * natH / FIG_H;
  const scaleMin = 0.50 * natH / FIG_H;

  const centeredHeadY = (s: number) => natH / 2 - (FIG_H / 2 - headTop) * s;

  const coreIdx    = [0, 5, 6, 11, 12, 15, 16];
  const clipMargin = 0.03;
  const bodyDetected = coreIdx.every(i => {
    const p = kpPx[i];
    return Array.isArray(p)
      && Number.isFinite(p[0]) && Number.isFinite(p[1])
      && p[0] > clipMargin * natW && p[0] < (1 - clipMargin) * natW
      && p[1] > clipMargin * natH && p[1] < (1 - clipMargin) * natH;
  });

  const [anchorX, anchorY] = getHeadAnchor(kpPx);
  const bbox           = getPoseBBox(kpPx);
  // The bbox spans nose to ankles, which flex shares with front — only FIG_H
  // differs, because of the raised hands. Scaling the front factor by the ratio
  // of figure heights cancels that out, so the flex body lands the same size on
  // screen as the front body instead of shrinking to make room for the hands.
  const frontFactor    = 1.16;
  const outlineTargetH = bbox.height * (
    view === "right" ? 1.26
      : view === "flex" ? frontFactor * (FIG_H_FLEX / FIG_H_FRONT)
      : frontFactor
  );
  const rawScale       = outlineTargetH / FIG_H;

  let headX: number, headY: number, scale: number, message: string | null = null;
  const cx = natW / 2;

  if (!bodyDetected) {
    scale   = scaleMax;
    headX   = cx;
    headY   = centeredHeadY(scale);
    message = rawScale > scaleMax ? "Step back" : "Stay in center";
  } else {
    scale   = Math.max(scaleMin, Math.min(scaleMax, rawScale));
    const cy = centeredHeadY(scale);
    headX = Math.max(cx - 0.25 * natW, Math.min(cx + 0.25 * natW, anchorX));
    headY = Math.max(cy - 0.25 * natH, Math.min(cy + 0.25 * natH, anchorY));
  }

  const silhouetteColor = poseOk
    ? "rgba(34,197,94,0.95)"
    : "rgba(245,158,11,0.95)";

  if (view === "right") {
    let spineX: number | undefined;
    if (bodyDetected) {
      const s5 = kpPx[5]; const s6 = kpPx[6];
      if (s5 && s6 && Number.isFinite(s5[0]) && Number.isFinite(s6[0])) {
        const raw = (s5[0] + s6[0]) / 2;
        spineX = Math.max(cx - 0.25 * natW, Math.min(cx + 0.25 * natW, raw));
      }
    }
    const needsFlip = flipped;
    if (needsFlip) {
      ctx.save();
      ctx.translate(natW, 0);
      ctx.scale(-1, 1);
    }
    drawSideSilhouette(ctx, headX, headY, scale, silhouetteColor, headRadiusMult, sideFatnessMult, spineX);
    if (needsFlip) ctx.restore();
  } else if (view === "flex") {
    drawFlexSilhouette(ctx, headX, headY, scale, silhouetteColor, headRadiusMult, torsoWidthMult);
  } else {
    drawFrontSilhouette(ctx, headX, headY, scale, silhouetteColor, headRadiusMult, torsoWidthMult);
  }

  if (message) {
    const fontSize = Math.round(natH * 0.045);
    ctx.save();
    ctx.font           = `bold ${fontSize}px sans-serif`;
    ctx.textAlign      = "center";
    ctx.textBaseline   = "middle";
    ctx.lineWidth      = fontSize * 0.15;
    ctx.strokeStyle    = "rgba(0,0,0,0.7)";
    ctx.fillStyle      = "rgba(239,68,68,1)";
    ctx.strokeText(message, natW / 2, natH * 0.11);
    // 11 %, not 6 %: at 6 % the instruction sat on the row of badges along
    // the top of the camera screen (FRONT VIEW, Debug) on every phone size.
    ctx.fillText(message,   natW / 2, natH * 0.11);
    ctx.restore();
  }
}
